import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import { AppUser } from 'Src/views/App/App';
import { Attachment } from 'Src/models/generalMessage';
import { HTMLEditorQuill, HTMLEditorQuillHandle } from '../HTMLEditorQuill/HTMLEditorQuill';
import { GeneralMessageVariableInsert } from '../GeneralMessageVariableInsert/GeneralMessageVariableInsert';
import { GeneralMessagePreviewModal } from '../GeneralMessagePreviewModal/GeneralMessagePreviewModal';
import { Loader } from '../Loader/Loader';
import { getFileName } from 'Src/helpers/file/file';
import { VariableTextInput, VariableTextInputHandle } from '../VariableTextInput/VariableTextInput';
import { UserSignatureInsert } from '../UserSignatureInsert/UserSignatureInsert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { HelpIconButton } from '../HelpIconButton/HelpIconButton';
import '../VariableTextInput/VariableTextInput.css';
import './GeneralMessageContentV2.css';

type ChannelTab = 'email' | 'push' | 'sms';

interface Props {
  user: AppUser;

  isEmailEnabled: boolean;
  isPushEnabled: boolean;
  isSmsEnabled: boolean;
  onEmailEnabledChange: (val: boolean) => void;
  onPushEnabledChange: (val: boolean) => void;
  onSmsEnabledChange: (val: boolean) => void;

  messageEmailSubject: string;
  messageEmailBody: string;
  messagePushTitle: string;
  messagePushBody: string;
  messageSmsText: string;
  messageAttachments: Attachment[];
  senderDisplayName: string;

  onEmailSubjectChange: (event: any) => void;
  onEmailBodyChange: (body: string) => void;
  onPushTitleChange: (event: any) => void;
  onPushBodyChange: (event: any) => void;
  onSmsTextChange: (event: any) => void;
  onSenderDisplayNameChange: (event: any) => void;
  onEmailAttachmentChange: (event: any) => void;
  onRemoveAttachment: (key: string) => void;
  onCloseImageError: () => void;

  showStudentVariables?: boolean;
  showParentVariables?: boolean;

  isError: boolean;
  isImageError: boolean;
  isLoadingFile: boolean;
  isEditable?: boolean;

  editorRef?: React.RefObject<HTMLEditorQuillHandle>;
  fromInputRef?: React.RefObject<VariableTextInputHandle>;
  subjectInputRef?: React.RefObject<VariableTextInputHandle>;
  pushTitleInputRef?: React.RefObject<HTMLInputElement>;
  pushBodyInputRef?: React.RefObject<HTMLTextAreaElement>;
  smsInputRef?: React.RefObject<HTMLTextAreaElement>;

  onInsertVariableToSubject?: (value: string) => void;
  onInsertVariableToFrom?: (value: string) => void;
  onInsertVariableToPushTitle?: (value: string) => void;
  onInsertVariableToPushBody?: (value: string) => void;
  onInsertVariableToSms?: (value: string) => void;
  onHelpClick?: () => void;
}

const SMS_LIMIT = 160;

export const GeneralMessageContentV2: FunctionComponent<Props> = props => {
  const {
    user,
    isEmailEnabled,
    isPushEnabled,
    isSmsEnabled,
    onEmailEnabledChange,
    onPushEnabledChange,
    onSmsEnabledChange,
    messageEmailSubject,
    messageEmailBody,
    messagePushTitle,
    messagePushBody,
    messageSmsText,
    messageAttachments,
    senderDisplayName,
    onEmailSubjectChange,
    onEmailBodyChange,
    onPushTitleChange,
    onPushBodyChange,
    onSmsTextChange,
    onSenderDisplayNameChange,
    onEmailAttachmentChange,
    onRemoveAttachment,
    onCloseImageError,
    showStudentVariables,
    showParentVariables,
    isError,
    isImageError,
    isLoadingFile,
    isEditable,
    editorRef,
    subjectInputRef,
    fromInputRef,
    pushTitleInputRef,
    pushBodyInputRef,
    smsInputRef,
    onInsertVariableToSubject,
    onInsertVariableToFrom,
    onInsertVariableToPushTitle,
    onInsertVariableToPushBody,
    onInsertVariableToSms
  } = props;

  const enabledChannels: ChannelTab[] = [
    ...(isEmailEnabled ? ['email' as ChannelTab] : []),
    ...(isPushEnabled ? ['push' as ChannelTab] : []),
    ...(isSmsEnabled ? ['sms' as ChannelTab] : [])
  ];

  const [activeTab, setActiveTab] = useState<ChannelTab>('email');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const effectiveTab: ChannelTab = enabledChannels.includes(activeTab) ? activeTab : enabledChannels[0] || 'email';
  const isDisabled = isEditable === false;

  const handleChannelToggle = (channel: ChannelTab, currentlyEnabled: boolean) => {
    const willBeEnabled = !currentlyEnabled;
    if (channel === 'email') onEmailEnabledChange(willBeEnabled);
    if (channel === 'push') onPushEnabledChange(willBeEnabled);
    if (channel === 'sms') onSmsEnabledChange(willBeEnabled);
    if (willBeEnabled) setActiveTab(channel);
    else if (effectiveTab === channel) {
      const remaining = enabledChannels.filter(c => c !== channel);
      if (remaining.length > 0) setActiveTab(remaining[0]);
    }
  };

  const channelConfig = [
    { key: 'email' as ChannelTab, label: 'Email', enabled: isEmailEnabled },
    { key: 'push' as ChannelTab, label: 'Push', enabled: isPushEnabled },
    { key: 'sms' as ChannelTab, label: 'SMS', enabled: isSmsEnabled }
  ];

  const renderChannelIcon = (channel: ChannelTab) => {
    switch (channel) {
      case 'email':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m4 7 8 6 8-6" />
          </svg>
        );
      case 'push':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="7" y="2.5" width="10" height="19" rx="2" />
            <path d="M10 5h4M11 18.5h2" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 15a3 3 0 0 1-3 3H8l-4 3v-6a3 3 0 0 1-1-2.2V7a3 3 0 0 1 3-3h11a3 3 0 0 1 3 3z" />
          </svg>
        );
    }
  };

  const renderChannelButton = (ch: typeof channelConfig[0]) => {
    const isActive = effectiveTab === ch.key && ch.enabled;

    return (
      <div
        key={ch.key}
        className={`gmComposeChannel ${ch.enabled ? 'gmComposeChannel--on' : ''} ${
          isActive ? 'gmComposeChannel--active' : ''
        }`}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        onClick={() => !isDisabled && (ch.enabled ? setActiveTab(ch.key) : handleChannelToggle(ch.key, false))}
        onKeyDown={event => {
          if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            ch.enabled ? setActiveTab(ch.key) : handleChannelToggle(ch.key, false);
          }
        }}
        aria-pressed={ch.enabled}
        aria-disabled={isDisabled}
      >
        <span className="gmComposeChannel__icon">{renderChannelIcon(ch.key)}</span>
        <span className="gmComposeChannel__label">{ch.label}</span>
        {ch.enabled ? (
          <span
            className="gmComposeChannel__check"
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            onClick={event => {
              event.stopPropagation();
              !isDisabled && handleChannelToggle(ch.key, true);
            }}
            onKeyDown={event => {
              if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                event.stopPropagation();
                handleChannelToggle(ch.key, true);
              }
            }}
            aria-label={`Disable ${ch.label}`}
            title={`Disable ${ch.label}`}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m6 12 4 4 8-9" />
            </svg>
          </span>
        ) : (
          <span className="gmComposeChannel__add">+ enable</span>
        )}
      </div>
    );
  };

  const smsCount = messageSmsText.length;
  const smsMsgCount = Math.ceil(smsCount / SMS_LIMIT) || 0;

  const renderEmailTab = () => (
    <div className="gmComposeFields">
      <div className="gmField">
        <div className="gmField__rowLabel">
          <label className="gmField__label">From (display name)</label>
          {onInsertVariableToFrom && (
            <GeneralMessageVariableInsert
              onInsert={onInsertVariableToFrom}
              showStudentVariables={false}
              showRecipientVariables={false}
              excludeVariables={['{{schoolLogo}}', '{{currentYear}}', '{{currentDate}}']}
            />
          )}
        </div>
        <VariableTextInput
          ref={fromInputRef}
          value={senderDisplayName || ''}
          onChange={val => onSenderDisplayNameChange({ target: { value: val } })}
          placeholder="e.g. Mrs Smith, Headteacher"
          disabled={isDisabled}
          className="gmVariableInput"
        />
      </div>

      <div className="gmField">
        <div className="gmField__rowLabel">
          <label className="gmField__label">Subject</label>
          {onInsertVariableToSubject && (
            <GeneralMessageVariableInsert
              onInsert={onInsertVariableToSubject}
              showStudentVariables={showStudentVariables}
              showRecipientVariables={showParentVariables}
              excludeVariables={['{{schoolLogo}}']}
            />
          )}
        </div>
        <VariableTextInput
          ref={subjectInputRef}
          value={messageEmailSubject}
          onChange={val => onEmailSubjectChange({ target: { value: val } })}
          placeholder="Email subject..."
          disabled={isDisabled}
          className="gmVariableInput"
        />
      </div>

      <div className="gmField">
        <div className="gmField__rowLabel">
          <label className="gmField__label">Body</label>
          <div className="gmField__tools">
            {editorRef && (
              <GeneralMessageVariableInsert
                onInsert={value => editorRef.current?.insertVariable(value)}
                showStudentVariables={showStudentVariables}
                showRecipientVariables={showParentVariables}
              />
            )}
            <UserSignatureInsert user={user} onInsert={html => editorRef?.current?.insertHtml(html)} />
          </div>
        </div>
        <div className="gmRichText">
          <HTMLEditorQuill
            user={user}
            ref={editorRef}
            onEmailBodyChange={onEmailBodyChange}
            value={messageEmailBody}
            isEnabled={isEmailEnabled && !isDisabled}
            placeholder="Write your email here..."
          />
        </div>
      </div>

      {!isDisabled && (
        <div className="gmField">
          <label className="gmField__label">Attachments</label>
          <label className="gmDropzone">
            <span className="gmDropzone__icon">
              <FontAwesomeIcon icon={faPaperclip} />
            </span>
            <span>Drag a file here or click to upload</span>
            <input
              type="file"
              onChange={onEmailAttachmentChange}
              disabled={isImageError || isError || isLoadingFile}
            />
          </label>
          {isLoadingFile && <Loader />}
          {isImageError && (
            <div className="gmErrorInline">
              Error uploading
              <button type="button" onClick={onCloseImageError}>
                Close
              </button>
            </div>
          )}
          {messageAttachments.map((attachment, index) => {
            const name = getFileName(attachment.fileName);
            const size = attachment.fileSize ? `${(attachment.fileSize / 1000).toFixed(0)} Kb` : '';
            return (
              <div key={index} className="gmAttachment">
                <span className="gmAttachment__icon">
                  <FontAwesomeIcon icon={faFileAlt} />
                </span>
                <span className="gmAttachment__name">{name}</span>
                <span className="gmAttachment__size">{size}</span>
                <button
                  type="button"
                  disabled={isImageError || isError}
                  onClick={() => onRemoveAttachment(attachment.externalFileId)}
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderPushTab = () => (
    <div className="gmComposeFields">
      <div className="gmField">
        <div className="gmField__rowLabel">
          <label className="gmField__label">Title</label>
          {onInsertVariableToPushTitle && (
            <GeneralMessageVariableInsert
              onInsert={onInsertVariableToPushTitle}
              showStudentVariables={showStudentVariables}
              showRecipientVariables={showParentVariables}
              excludeVariables={['{{schoolLogo}}']}
            />
          )}
        </div>
        <input
          ref={pushTitleInputRef}
          type="text"
          className="gmInput"
          value={messagePushTitle}
          onChange={onPushTitleChange}
          disabled={isDisabled}
          placeholder="Push notification title..."
        />
      </div>
      <div className="gmField">
        <div className="gmField__rowLabel">
          <label className="gmField__label">Body</label>
          {onInsertVariableToPushBody && (
            <GeneralMessageVariableInsert
              onInsert={onInsertVariableToPushBody}
              showStudentVariables={showStudentVariables}
              showRecipientVariables={showParentVariables}
              excludeVariables={['{{schoolLogo}}']}
            />
          )}
        </div>
        <textarea
          ref={pushBodyInputRef}
          rows={7}
          className="gmTextarea"
          value={messagePushBody}
          onChange={onPushBodyChange}
          disabled={isDisabled}
          placeholder="Push notification body..."
        />
      </div>
    </div>
  );

  const renderSmsTab = () => (
    <div className="gmComposeFields">
      <div className="gmField">
        <div className="gmField__rowLabel">
          <label className="gmField__label">Text</label>
          {onInsertVariableToSms && (
            <GeneralMessageVariableInsert
              onInsert={onInsertVariableToSms}
              showStudentVariables={showStudentVariables}
              showRecipientVariables={showParentVariables}
              excludeVariables={['{{schoolLogo}}']}
            />
          )}
        </div>
        <textarea
          ref={smsInputRef}
          rows={7}
          className="gmTextarea"
          value={messageSmsText}
          onChange={onSmsTextChange}
          disabled={isDisabled}
          placeholder="SMS text..."
        />
        <div className="gmSmsCount">
          <b>{smsCount}</b> {smsCount === 1 ? 'character' : 'characters'} - <b>{smsMsgCount}</b>{' '}
          {smsMsgCount === 1 ? 'message' : 'messages'} per recipient
        </div>
      </div>
    </div>
  );

  return (
    <div className="gmContentV2">
      <div className="gmContentV2__topbar">
        <div className="gmComposeChannels">{channelConfig.map(renderChannelButton)}</div>
        <div className="gmContentV2__topbarActions">
          <button type="button" className="gmButton gmButton--secondary" onClick={() => setIsPreviewOpen(true)}>
            Preview message
          </button>
          {props.onHelpClick && <HelpIconButton onClick={props.onHelpClick} label="Need help with this step?" />}
        </div>
      </div>

      {enabledChannels.length === 0 && (
        <div className="gmBanner gmBanner--warning">Enable at least one channel to send a message.</div>
      )}

      {enabledChannels.length > 0 && (
        <div className="gmComposeCard">
          {effectiveTab === 'email' && renderEmailTab()}
          {effectiveTab === 'push' && renderPushTab()}
          {effectiveTab === 'sms' && renderSmsTab()}
        </div>
      )}

      <GeneralMessagePreviewModal
        user={user}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        emailSubject={messageEmailSubject}
        emailBody={messageEmailBody}
        pushTitle={messagePushTitle}
        pushBody={messagePushBody}
        smsText={messageSmsText}
        senderDisplayName={senderDisplayName}
        isEmailEnabled={isEmailEnabled}
        isPushEnabled={isPushEnabled}
        isSmsEnabled={isSmsEnabled}
      />
    </div>
  );
};

GeneralMessageContentV2.displayName = 'GeneralMessageContentV2';
